import { useState, useMemo, useCallback, useEffect } from 'react'
import { useToast } from '@t-one-internal/design-system'
import { useRecognitionStore } from '../../../../stores/recognitionStore'
import { recognitionService } from '../../../../services/recognitionService'
import { useAuth } from '../../../../context/AuthContextBackend'
import type { Employee, RecognitionType, CompanyValue } from '../../../../types/recognition'
import type { CompanyValueOption } from '../../../../api/recognitionApi'
import { recognitionTypes } from '../../mockData'

export interface FilterField {
  key: string
  label: string
  options: string[]
}

export function useGiveRecognitionForm(onClose: () => void) {
  const { toast } = useToast()
  const { user } = useAuth()
  const refreshFeed = useRecognitionStore((s) => s.refreshFeed)

  // Spot Award is only available to managers and above (roles from /me endpoint)
  const canGiveSpotAward = !!(
    user?.is_rc_manager ||
    user?.is_rc_executive ||
    user?.is_rc_hrbp ||
    user?.is_hrmanager ||
    user?.is_superadmin ||
    user?.is_admin
  )

  const availableTypes = useMemo(
    () => recognitionTypes.filter((t) => t.id !== 'spot_award' || canGiveSpotAward),
    [canGiveSpotAward],
  )

  const [step, setStep] = useState(1)
  const [selectedRecipients, setSelectedRecipients] = useState<Employee[]>([])
  const [selectedType, setSelectedType] = useState<RecognitionType | null>(null)
  const [message, setMessage] = useState('')
  const [selectedValue, setSelectedValue] = useState<CompanyValue | null>(null)
  const [search, setSearch] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companyValueOptions, setCompanyValueOptions] = useState<CompanyValueOption[]>([])

  // Unified filter state: field key → selected values
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})

  // Load company values with IDs on mount
  useEffect(() => {
    recognitionService.getCompanyValues().then(setCompanyValueOptions).catch(() => { })
  }, [])

  // Load employees based on selected recognition type (RBAC)
  const loadEmployees = useCallback(async (type?: RecognitionType) => {
    const effectiveType = type || selectedType
    if (!effectiveType) return
    const result = await recognitionService.getEmployeesForRecognitionType(effectiveType)
    setEmployees(result)
  }, [selectedType])

  // When type changes: update type, clear recipients, and reload employees
  const handleTypeChange = useCallback((type: RecognitionType) => {
    setSelectedType(type)
    setSelectedRecipients([])
    setSearch('')
    setActiveFilters({})
    recognitionService.getEmployeesForRecognitionType(type).then(setEmployees)
  }, [])

  // Derive filter fields dynamically from loaded employees (same fields as Workforce filter)
  const filterFields = useMemo((): FilterField[] => {
    const orgLevels = new Map<string, Set<string>>() // levelName → scope_names
    const jobTitles = new Set<string>()
    const empTypes = new Set<string>()

    employees.forEach((emp) => {
      // Extract org hierarchy levels from org_assignments (mapped from org_path)
      emp.org_assignments?.forEach((a) => {
        if (a.level_name && a.scope_name && a.level_name !== 'Organization') {
          if (!orgLevels.has(a.level_name)) orgLevels.set(a.level_name, new Set())
          orgLevels.get(a.level_name)!.add(a.scope_name)
        }
      })
      if (emp.title) jobTitles.add(emp.title)
      if (emp.employment_type) empTypes.add(emp.employment_type)
    })

    const fields: FilterField[] = []

    // Org hierarchy levels in display order
    const orgLevelOrder = ['Business Unit', 'Division', 'Department', 'Team', 'Sub Team']
    orgLevelOrder.forEach((level) => {
      const options = orgLevels.get(level)
      if (options && options.size > 0) {
        fields.push({ key: `org_${level}`, label: level, options: [...options].sort() })
      }
    })

    // Fall back to primary unit name if no org hierarchy — only if there are multiple distinct values
    if (fields.length === 0) {
      const units = [...new Set(employees.map((e) => e.team).filter(Boolean))].sort()
      if (units.length > 1) fields.push({ key: 'team', label: 'Team', options: units })
    }

    if (jobTitles.size > 1)
      fields.push({ key: 'job_title', label: 'Job Title', options: [...jobTitles].sort() })
    if (empTypes.size > 1)
      fields.push({ key: 'employment_type', label: 'Employment Type', options: [...empTypes].sort() })

    return fields
  }, [employees])

  const filteredEmployees = useMemo(() => {
    let list = employees

    // Name / email search
    if (search) {
      const s = search.toLowerCase()
      list = list.filter((e) => e.name.toLowerCase().includes(s))
    }

    // Apply each active filter
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (values.length === 0) return
      if (key.startsWith('org_')) {
        const levelName = key.replace('org_', '')
        list = list.filter((e) =>
          e.org_assignments?.some((a) => a.level_name === levelName && values.includes(a.scope_name))
        )
      } else if (key === 'team') {
        list = list.filter((e) => values.includes(e.team))
      } else if (key === 'job_title') {
        list = list.filter((e) => values.includes(e.title))
      } else if (key === 'employment_type') {
        list = list.filter((e) => !!e.employment_type && values.includes(e.employment_type))
      }
    })

    return list
  }, [employees, search, activeFilters])

  const totalActiveFilters = useMemo(
    () => Object.values(activeFilters).reduce((sum, v) => sum + v.length, 0),
    [activeFilters],
  )

  // Shout Out is team recognition — requires at least 2 recipients
  const isShoutOut = selectedType === 'shoutout'
  const minRecipients = isShoutOut ? 2 : 1

  // Recipient actions
  const toggleRecipient = useCallback((employee: Employee) => {
    setSelectedRecipients((prev) => {
      const exists = prev.some((e) => e.id === employee.id)
      if (exists) return prev.filter((e) => e.id !== employee.id)
      return [...prev, employee]
    })
  }, [])

  const removeRecipient = useCallback((employeeId: string) => {
    setSelectedRecipients((prev) => prev.filter((e) => e.id !== employeeId))
  }, [])

  const selectAllVisible = useCallback(() => {
    setSelectedRecipients((prev) => {
      const prevIds = new Set(prev.map((e) => e.id))
      const allVisibleSelected = filteredEmployees.every((e) => prevIds.has(e.id))
      if (allVisibleSelected) {
        const visibleIds = new Set(filteredEmployees.map((e) => e.id))
        return prev.filter((e) => !visibleIds.has(e.id))
      }
      const newRecipients = [...prev]
      filteredEmployees.forEach((emp) => {
        if (!prevIds.has(emp.id)) newRecipients.push(emp)
      })
      return newRecipients
    })
  }, [filteredEmployees])

  const clearRecipients = useCallback(() => setSelectedRecipients([]), [])

  const canProceed = useCallback(() => {
    if (step === 1) return !!selectedType
    if (step === 2) return selectedRecipients.length >= minRecipients
    if (step === 3) return message.trim().length > 0
    return true
  }, [step, selectedRecipients, selectedType, message, minRecipients])

  const goNext = useCallback(() => {
    if (step < 4 && canProceed()) setStep(step + 1)
  }, [step, canProceed])

  const goBack = useCallback(() => {
    if (step > 1) setStep(step - 1)
  }, [step])

  const resetForm = useCallback(() => {
    setStep(1)
    setSelectedRecipients([])
    setSelectedType(null)
    setMessage('')
    setSelectedValue(null)
    setSearch('')
    setActiveFilters({})
    setShowSuccess(false)
    setIsSubmitting(false)
  }, [])

  const handleSend = useCallback(async () => {
    if (selectedRecipients.length === 0 || !selectedType || !message.trim()) return

    setIsSubmitting(true)
    try {
      const matchedValue = companyValueOptions.find((v) => v.name === selectedValue)
      const valueIds = matchedValue ? [matchedValue.id] : []

      await recognitionService.createRecognition({
        recipient_ids: selectedRecipients.map((r) => r.id),
        type: selectedType,
        message: message.trim(),
        value: selectedValue || '',
        value_ids: valueIds,
      })
      refreshFeed()

      setShowSuccess(true)
      setTimeout(() => {
        setShowSuccess(false)
        resetForm()
        onClose()
      }, 2500)
    } catch {
      toast({ title: 'Error', description: 'Failed to send recognition', variant: 'destructive' })
      setIsSubmitting(false)
    }
  }, [selectedRecipients, selectedType, message, selectedValue, companyValueOptions, refreshFeed, resetForm, onClose, toast])

  return {
    // State
    step,
    selectedRecipients,
    selectedType,
    message,
    selectedValue,
    search,
    showSuccess,
    filteredEmployees,
    isSubmitting,
    activeFilters,
    totalActiveFilters,
    filterFields,
    availableTypes,
    companyValueOptions,
    isShoutOut,
    minRecipients,

    // Setters
    handleTypeChange,
    setMessage,
    setSelectedValue,
    setSearch,
    setActiveFilters,

    // Recipient actions
    toggleRecipient,
    removeRecipient,
    selectAllVisible,
    clearRecipients,

    // Actions
    goNext,
    goBack,
    canProceed,
    handleSend,
    resetForm,
    loadEmployees,
    handleValueCreated: (newVal: CompanyValueOption) => {
      setCompanyValueOptions((prev) => [...prev, newVal])
      setSelectedValue(newVal.name as CompanyValue)
    },
    handleValueDeleted: async (valueId: string) => {
      await recognitionService.deleteCompanyValue(valueId)
      setCompanyValueOptions((prev) => prev.filter((v) => v.id !== valueId))
      // Clear selection if the deleted value was selected
      const deleted = companyValueOptions.find((v) => v.id === valueId)
      if (deleted && selectedValue === deleted.name) {
        setSelectedValue(null)
      }
    },
  }
}
